/**
*
* useTopicHeader
*
*/

import { useState } from 'react';
import PropTypes from 'prop-types';
import { pick } from 'lodash-es';
import useAuthorizedRequest from 'containers/Api2/useAuthorizedRequest.js';
import useCurrentRole from 'containers/User/useCurrentRole.js';
import useDialog from 'containers/Dialog/useDialog.js';
import toBeImplemented from 'components/dashboard/toBeImplemented.js';
import useUpdateTopics from '../useUpdateTopics.js';
import messages from '../messages.js';
import useConversationsContext from '../useConversationsContext.js';

function useTopicHeader() {
  const [showRcpts, setShowRcpts] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const role = useCurrentRole();
  const { baseUrl, topic, setInTopics, topics, setTopics } = useConversationsContext();
  const updateTopics = useUpdateTopics({ baseUrl, topics, setTopics });
  const { dlgClick } = useDialog();
  const { authFetch } = useAuthorizedRequest();

  const { id: topicId, archived, permissions } = topic;
  const { close, edit, leave, participants } = permissions;

  async function handleSubmitRcpts(otherParticipants) {
    const participantsWithMe = [{ id: role.id, roleType: role.type }, ...otherParticipants];
    const data = await authFetch({
      method: 'PUT',
      url: `${baseUrl}/${topicId}/participants`,
      body: {
        participants: participantsWithMe.map((p) => pick(p, ['id', 'roleType'])),
      },
    });
    setInTopics([topicId], (prev) => ({ ...prev, ...data }));
  }

  async function handleSubmitTitle(title) {
    const data = await authFetch({
      method: 'PUT',
      url: `${baseUrl}/${topicId}/title`,
      body: {
        // ...topic,
        title,
      },
      merge: true,
      withPromise: true,
    });
    setInTopics([topicId], (prev) => ({ ...prev, ...data }));
    topic.subTopicIds?.forEach((subTopicId) => {
      setInTopics([subTopicId, 'title'], title);
    });
    updateTopics();
    setEditTitle(false);
  }

  function cancelEditTitle() {
    setEditTitle(false);
  }

  async function toggleProperty(property, postfix, noReport = []) {
    const url = `${baseUrl}/${topicId}/${postfix}`;
    const oldVal = topic[property];
    try {
      if (oldVal) {
        setInTopics([topicId, property], null);
        await authFetch({ method: 'DELETE', url, noReport });
      } else {
        setInTopics([topicId, property], new Date().toISOString());
        await authFetch({ method: 'POST', url, noReport });
      }
    } catch (error) {
      if (noReport.includes(error.response.status)) {
        return;
      }
      setInTopics([topicId, property], oldVal);
      throw error;
    }
  }

  function handleClose() {
    dlgClick(
      { message: messages.confirmCloseTopic, confirmStyle: 'danger', confirmLabel: messages.closeTopic },
      () => toggleProperty('closed', 'close'),
    );
  }

  async function handleArchive() {
    if (archived) {
      toggleProperty('archived', 'archive');
    } else {
      dlgClick(
        { message: messages.confirmArchiveTopic, confirmStyle: 'danger', confirmLabel: messages.archiveTopic },
        () => toggleProperty('archived', 'archive'),
      );
    }
  }

  function toggleFlagged() {
    toggleProperty('flagged', 'flagged');
  }

  const menuItems = [
    { label: messages.editSubject, handler: () => setEditTitle(true), disabled: !edit },
    { label: messages.changeParticipants, handler: () => setShowRcpts(true), disabled: !participants },
    { label: messages.leaveTopic, handler: toBeImplemented, disabled: !leave },
    { label: messages.closeTopic, handler: handleClose, disabled: !close },
    { label: archived ? messages.unarchiveTopic : messages.archiveTopic, handler: handleArchive },
  ];
  return { topicId, menuItems, showRcpts, toggleFlagged, onHide: () => setShowRcpts(false), handleSubmitRcpts, editTitle, handleSubmitTitle, cancelEditTitle };
}

useTopicHeader.propTypes = {
  topicId: PropTypes.string.isRequired,
};

export default useTopicHeader;
